import { useQuery } from '@tanstack/react-query';

import { MARKETING_COPY_FALLBACK } from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';

// Marketing copy is near-static, so clients only need to re-poll infrequently.
// A long staleTime stops every active tab from re-fetching every few minutes,
// which (together with the edge cache on /api/marketing-copy) keeps Sanity CDN
// requests low and largely decoupled from traffic. See FFH-1411.
const ONE_HOUR_IN_MS = 60 * 60 * 1000;
const NINETY_MINUTES_IN_MS = 90 * 60 * 1000;

const fetchMarketingCopy = async (): Promise<MarketingCopy> => {
  const response = await fetch('/api/marketing-copy');
  if (!response.ok) {
    throw new Error(`Failed to fetch marketing copy: ${response.status}`);
  }
  return response.json();
};

export const useMarketingCopy = (): MarketingCopy => {
  const { data } = useQuery({
    queryKey: ['marketingCopy'],
    queryFn: fetchMarketingCopy,
    staleTime: ONE_HOUR_IN_MS,
    cacheTime: NINETY_MINUTES_IN_MS,
    initialData: MARKETING_COPY_FALLBACK,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false
  });

  return data;
};
